import createClient from "openapi-fetch";

import type { paths } from "./schema";

/**
 * The typed API client — ARCHITECTURE §10.
 *
 * `paths` is generated from FastAPI's OpenAPI schema, which is generated from the
 * Pydantic models. Nothing in this directory is written by hand except this file and
 * the query hooks, so a field renamed in `apps/api/schemas.py` becomes a TypeScript
 * error here rather than `undefined` at runtime.
 *
 * The base URL is always `/api` and never configurable: in development Vite proxies
 * it to the API process, in production nginx does (§2). A frontend that knows the
 * backend's address is a frontend with an environment variable to get wrong.
 */
export const api = createClient<paths>({ baseUrl: "/api" });

/** What the API returns when a request is rejected — FastAPI's `{"detail": ...}`. */
interface ProblemBody {
  detail?: unknown;
}

/**
 * Turn a failed response into a message worth showing.
 *
 * §9 says errors name the fix, and the use cases below the API already do: "question
 * 12 is approved; only pending_review/stale can be approved" is the actual string
 * `review.approve` raises. This unwraps it rather than replacing it with "Bad
 * Request", which would throw away the only useful part.
 */
export function problemMessage(error: unknown, fallback = "Something went wrong"): string {
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "detail" in error) {
    const { detail } = error as ProblemBody;
    if (typeof detail === "string") return detail;
    // 422 from FastAPI's validator: a list of {loc, msg, type}.
    if (Array.isArray(detail)) {
      const messages = detail
        .map((d) => (d && typeof d === "object" && "msg" in d ? String((d as { msg: unknown }).msg) : null))
        .filter((m): m is string => m !== null);
      if (messages.length > 0) return messages.join("; ");
    }
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

/** Throws with the API's own message, so callers can just `await` and let it bubble. */
export function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) throw new Error(problemMessage(result.error));
  if (result.data === undefined) throw new Error("The API returned no body");
  return result.data;
}
